import { SelectItem } from '@/types/ui';
import { Dispatch, JSX, RefObject, SetStateAction, type ReactNode, type SyntheticEvent } from 'react';

const DROPDOWN_LIST_MAX_HEIGHT = 160;

export type DropdownPosition = 'up' | 'down';

export function handleDropdownListPosition(
  dropdownPosition: DropdownPosition | undefined,
  dropdownRef: RefObject<HTMLElement | null>,
  setDropdownListPosition: Dispatch<SetStateAction<DropdownPosition>>,
) {
  if (dropdownPosition) return;
  const dropdown = dropdownRef.current;
  if (dropdown) {
    const rect = dropdown.getBoundingClientRect();
    const spaceBelow = window.innerHeight - (rect.bottom + DROPDOWN_LIST_MAX_HEIGHT);
    const spaceAbove = rect.top;
    if (spaceBelow < dropdown.offsetHeight && spaceAbove > spaceBelow) {
      setDropdownListPosition('up');
    } else {
      setDropdownListPosition('down');
    }
  }
}

export function isOptionSelected<T>(optionValue: SelectItem<T>, fixedValue: Array<T>) {
  if (optionValue.value?.hasOwnProperty('period')) {
    const periodEntry = Object.entries(optionValue.value).find(([key]) => key === 'period');
    const periodValue = periodEntry?.[1];
    return !!fixedValue.find(value => value != null && Object.values(value).find(v => v === periodValue));
  }
  if (!optionValue.value?.hasOwnProperty('period')) {
    return !!fixedValue.find(value => JSON.stringify(value) === JSON.stringify(optionValue.value));
  }
}

export const createStableObjectHash = (obj: object) => {
  return JSON.stringify(obj, Object.keys(obj).sort());
};

export const createSelectItemKey = <T>(option: SelectItem<T>) => {
  const valueHash =
    typeof option.value === 'object' && option.value !== null
      ? createStableObjectHash(option.value)
      : String(option.value);
  return `${valueHash}-${option.category || 'default'}`;
};

/** Modifier keys read from a native or synthetic pointer event (keyboard-safe in jsdom). */
export type PointerModifierState = Readonly<{
  shiftKey: boolean;
  ctrlKey: boolean;
}>;

function readBooleanModifierKey(target: object, key: 'shiftKey' | 'ctrlKey'): boolean {
  if (!Reflect.has(target, key)) {
    return false;
  }
  const value = Reflect.get(target, key);
  return Boolean(value);
}

/**
 * Reads shift/ctrl from a native event without `instanceof MouseEvent`.
 * Jest/jsdom may not define the global `MouseEvent`, which makes `instanceof` throw.
 */
export function getPointerModifiersFromNativeEvent(nativeEvent: Event | undefined | null): PointerModifierState {
  if (nativeEvent == null || typeof nativeEvent !== 'object') {
    return { shiftKey: false, ctrlKey: false };
  }
  return {
    shiftKey: readBooleanModifierKey(nativeEvent, 'shiftKey'),
    ctrlKey: readBooleanModifierKey(nativeEvent, 'ctrlKey'),
  };
}

/** Same as {@link getPointerModifiersFromNativeEvent} for React synthetic events. */
export function getPointerModifiersFromSyntheticEvent(
  synthetic: SyntheticEvent<Element, Event> | null | undefined,
): PointerModifierState {
  return getPointerModifiersFromNativeEvent(synthetic?.nativeEvent ?? null);
}

export function hasAltLabel<T>(item: SelectItem<T>): item is SelectItem<T> & { label: JSX.Element; altLabel: string } {
  return 'altLabel' in item;
}

/** Plain string for tooltip, aria, or `TreeItem` `label` when the visible label is JSX. */
export function extractSelectItemLabelText<T>(option: SelectItem<T> | null | undefined): string {
  if (option == null) {
    return '';
  }
  if (typeof option.label === 'string') {
    return option.label;
  }
  if ('altLabel' in option && typeof option.altLabel === 'string') {
    return option.altLabel;
  }
  return '';
}

/** Rich label node for `TreeItem` `labelContent`; `undefined` when `label` is already a string. */
export function getSelectItemLabelContent<T>(option: SelectItem<T>): ReactNode | undefined {
  return typeof option.label === 'string' ? undefined : option.label;
}

/** Filter out options with null value/label, falling back to unfilteredOptions when options is empty. */
export function getFixedOptions<T>(
  options: SelectItem<T>[] | null | undefined,
  unfilteredOptions?: SelectItem<T>[] | null,
): SelectItem<T>[] {
  const validFilter = (opt: SelectItem<T>) => opt.value != null && opt.label != null;
  const nonNullOptions = options?.filter(validFilter) ?? [];
  if (nonNullOptions.length > 0) return nonNullOptions;
  return unfilteredOptions?.filter(validFilter) ?? [];
}
